import { DialogM } from "@common/components/alert/dialog/Dialog"
import TextM from "@common/components/text/TextM"
import TitleM from "@common/components/text/TitleM"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { useTutorScore } from "@common/hooks/useTutorScore"
import { theme } from "@common/theme/theme"
import { DimensionsContext, IS_WEB } from "@common/utils/mobileUtils"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { authService } from "@data/services/authService"
import { groupAttributionService } from "@data/services/groupAttributionService"
import { liveService } from "@data/services/liveService"
import { substitutionProposalsService } from "@data/services/substitutionProposalsService"
import { tutorsService } from "@data/services/tutorsService"
import { hasRole } from "@data/utility/ability"
import { AbilityContext } from "@data/utility/Can"
import { useIsFocused } from "@react-navigation/native"
import { StatusBar } from "@common/systemBars/StatusBar"
import React, { useContext, useEffect, useState } from "react"
import {
    ActivityIndicator,
    Linking,
    RefreshControl,
    ScrollView,
    StyleSheet,
    View,
} from "react-native"
import { IconButton } from "react-native-paper"
import { connect } from "react-redux"
import MaDispoMonthlyReminder from "../availability/MaDispoMonthlyReminder"
import TutorRankingResetModal from "../components/TutorRankingResetModal"
import TutorNextLivesCard from "../lives/TutorNextLivesCard"
import TutorTaskBanner from "./components/TutorTaskBanner"
import TrainingCompletionCard from "./TrainingCompletionCard"
import CoachStats from "./dashboard/CoachStats"
import TutorScoreCardM from "./dashboard/TutorScoreCardM"

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.whiteAlmost,
        alignItems: "center",
        justifyContent: "flex-start",
        alignSelf: "center",
    },
})

const TutorHomeTabScreen = (props) => {
    const dimensions = useContext(DimensionsContext)
    const isFocused = useIsFocused()

    const [pageLoaded, setPageLoaded] = useState(false)
    const [isFirstLoading, setFirstLoading] = useState(true)

    // Tutor score is fetched automatically with same-day cache (unified endpoint)
    const {
        shouldFetch: shouldFetchTutorScore,
        fetchTutorScore,
        isLoading: tutorScoreLoading,
    } = useTutorScore(props.user?.id, false, "ALL")

    useEffect(() => {
        setTimeout(() => {
            setPageLoaded(true)
        }, 2000)
    }, [])

    useEffect(() => {
        if (pageLoaded && !props.getUserRequest) {
            setFirstLoading(false)
        }
    }, [props.getUserRequest, pageLoaded])

    useEffect(() => {
        if (shouldFetchTutorScore) {
            fetchTutorScore()
        }
    }, [shouldFetchTutorScore])

    // Refetch tasks when screen comes into focus (e.g., returning from PrepareLiveScreen)
    useEffect(() => {
        if (isFocused && !isFirstLoading) {
            props.apiRequest(tutorsService.getTutorTasks, {}, {}, {})
        }
    }, [isFocused])

    // Handle cancelSubstitution errors (e.g., group attribution linked)
    useApiStatus(
        substitutionProposalsService.cancelSubstitution,
        false,
        false,
        () => {
            props.apiRequest(authService.getUser)
            props.apiRequest(liveService.getLiveSessions)
        }, // success callback
        (error) => {
            // Handle 400 error for group attribution linked substitutions
            if (error?.status === 400) {
                const whatsappUrl =
                    props.supportWhatsappUrl ||
                    "https://wa.me/message/FCOBQ6PCPMBJC1"
                DialogM.show({
                    text1: "Impossible d'annuler",
                    text2: "Ce remplacement est lié à une demande d'absence sur plusieurs séances, et ne peut pas être annulé ici. Contacte l'équipe Masteur.",
                    buttonText1: "Contacter le support",
                    buttonColor1: theme.colors.primary,
                    onPress: () => {
                        if (IS_WEB) {
                            window.open(
                                whatsappUrl,
                                "_blank",
                                "noopener,noreferrer"
                            )
                        } else {
                            Linking.openURL(whatsappUrl).catch((err) =>
                                console.error("Couldn't load page", err)
                            )
                        }
                    },
                    buttonText2: "Retour",
                })
            }
            props.apiIdle(substitutionProposalsService.cancelSubstitution)
        } // error callback
    )

    const request = !!(
        props.getUserRequest ||
        tutorScoreLoading ||
        isFirstLoading
    )

    const ability = useContext(AbilityContext)

    // Show loading only if user data is not loaded yet
    if (isFirstLoading && !props.user) {
        return (
            <View
                style={{
                    flex: 1,
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <ActivityIndicator size="large" color={theme.colors.primary} />
            </View>
        )
    }
    return (
        <ScrollView
            refreshControl={
                <RefreshControl
                    refreshing={request}
                    onRefresh={() => {
                        props.apiRequest(authService.getUser)
                        props.apiRequest(
                            groupAttributionService.getProposalsCount
                        )
                        fetchTutorScore()
                        // Refetch tasks when user pulls to refresh
                        props.apiRequest(
                            tutorsService.getTutorTasks,
                            {},
                            {},
                            {}
                        )
                    }}
                />
            }
        >
            <View style={[styles.container]}>
                <>
                    {/* ** Tutor candidature validation status */}
                    {hasRole(props.user, "tutor-candidate") &&
                        !!props.user?.candidature_sent_at && (
                            <View
                                style={{
                                    width: dimensions.width - 40,
                                    borderRadius: 15,
                                    alignItems: "flex-start",
                                    justifyContent: "flex-start",
                                    padding: 15,
                                    marginTop: 20,
                                    backgroundColor: theme.colors.warningLight,
                                }}
                            >
                                <TextM>
                                    {props.user?.candidature_validated_at
                                        ? "Candidature validée"
                                        : "Candidature envoyée"}
                                </TextM>
                            </View>
                        )}
                    {/* ** Tutor Task Banner - Groups all tasks - Show immediately */}
                    {ability?.can("read", "my_tutor_profile_completion") &&
                        props.user?.id && <TutorTaskBanner />}

                    {/* ** Formation (video + quiz) — new tutors only, until passed */}
                    {hasRole(props.user, "tutor") && <TrainingCompletionCard />}

                    {/* ** Monthly « Ma dispo » reminder (renders nothing; fires a
                        once-per-month popup when the week is stale) */}
                    {hasRole(props.user, "tutor") && <MaDispoMonthlyReminder />}

                    <View
                        style={{
                            flexDirection: "row",
                            alignItems: "center",
                            alignSelf: "center",
                            justifyContent: "space-between",
                            width: dimensions?.width,
                            borderColor: theme.colors.lightMore,
                            marginTop: 20,
                            marginBottom: 20,
                        }}
                    >
                        <View style={{ marginLeft: 20 }}>
                            <TitleM fontWeight="ExtraBold">
                                TABLEAU DE BORD
                            </TitleM>
                        </View>

                        <View
                            style={{
                                flexDirection: "row",
                                alignItems: "center",
                                marginRight: 10,
                            }}
                        >
                            <>
                                <IconButton
                                    onPress={() => {
                                        props.apiRequest(authService.getUser)
                                        props.apiRequest(
                                            groupAttributionService.getProposalsCount
                                        )
                                        fetchTutorScore()
                                        // Refetch tasks on manual refresh
                                        props.apiRequest(
                                            tutorsService.getTutorTasks,
                                            {},
                                            {},
                                            {}
                                        )
                                    }}
                                    loading={IS_WEB && request}
                                    iconColor={theme.colors.primary}
                                    containerColor={theme.colors.transparent}
                                    disabled={request}
                                    icon={"sync-alt"}
                                    size={20}
                                />
                            </>
                        </View>
                    </View>

                    {/* ** Tutor interviews and stats */}
                    {ability?.can("read", "my_students") && (
                        <>
                            {/*<TutorNextInterviewsCard/>*/}
                            <TutorNextLivesCard />

                            {/* Show monthly score for tutors */}
                            {hasRole(props.user, "tutor") && (
                                <TutorScoreCardM
                                    tutorScore={props.monthlyScore}
                                    previousMonthScore={
                                        props.previousMonthScore
                                    }
                                    globalScore={props.globalScore}
                                    bonusInfo={props.bonusInfo}
                                    trialConversionStats={
                                        props.trialConversionStats
                                    }
                                    previousMonthTrialConversionStats={
                                        props.previousMonthTrialConversionStats
                                    }
                                    globalTrialConversionStats={
                                        props.globalTrialConversionStats
                                    }
                                    width={dimensions?.width}
                                    title={null}
                                    isMonthly={true}
                                    isAdmin={false}
                                />
                            )}
                            <CoachStats />
                        </>
                    )}

                    {/* ** Placeholder for users who are not tutors */}
                    {!ability?.can("read", "my_students") && (
                        <>
                            <TextM style={{ marginTop: 10 }}>
                                Bientôt disponible
                            </TextM>
                        </>
                    )}
                    {/* ** Admin & superAdmin Analytics */}
                    {ability?.can("read", "analytics") && <></>}
                </>
                <StatusBar
                    style="dark"
                    backgroundColor={theme.colors.white}
                    translucent={false}
                />
            </View>
            <TutorRankingResetModal />
        </ScrollView>
    )
}

const mapStateToProps = (state) => {
    return {
        user: state.data.currentUser?.object,
        monthlyScore: state.data.tutorMonthlyScore?.myMonthlyScore,
        previousMonthScore: state.data.tutorMonthlyScore?.myPreviousMonthScore,
        globalScore: state.data.tutorMonthlyScore?.myGlobalScore,
        bonusInfo: state.data.tutorMonthlyScore?.bonusInfo,
        trialConversionStats:
            state.data.tutorMonthlyScore?.trialConversionStats,
        previousMonthTrialConversionStats:
            state.data.tutorMonthlyScore?.previousMonthTrialConversionStats,
        globalTrialConversionStats:
            state.data.tutorMonthlyScore?.globalTrialConversionStats,
        getUserRequest: state.api.getUser?.request,
        getSkillSetsRequest: state.api.getSkillSets?.request,
        supportWhatsappUrl: state.data.staticData?.support_whatsapp_url,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
}

export default connect(mapStateToProps, mapDispatchToProps)(TutorHomeTabScreen)
